import type { ProductDTO, RatingData } from '@/types/products';

import { buildGA4ItemFromProduct, pushGA4EcommerceEvent } from '@/utils/gtmEcommerce';

export default function useModeDetails() {
  function scrollToWidget(event: Event) {
    event.preventDefault();
    const component = document.querySelector('yb-details');
    const hasDisplayedWidget = component?.shadowRoot?.getElementById('cta_scroll_target');
    if (hasDisplayedWidget) {
      hasDisplayedWidget?.scrollIntoView({ behavior: 'smooth' });
    }
  }

  function scrollToFairguestWidget() {
    const component = document.querySelector('yb-details');
    const widget = component?.shadowRoot?.getElementById('fairguest_wrapper');
    widget?.scrollIntoView({ behavior: 'smooth' });
  }

  function dispatchFavoriteEvent(product?: ProductDTO) {
    const evt = new Event('update', { bubbles: true, cancelable: false, composed: true });
    const woodyCart = document.querySelector('.woody-favorites-cart');
    if (!woodyCart) {
      console.warn('No woody cart widget found.');
      return;
    }
    const favorites = window.localStorage.getItem('woody_favorites');
    // @ts-expect-error globals is unknown
    const postId = window.globals?.post_id;
    const newFavorite = { [`${postId}`]: 'f' };
    const payload = favorites ? JSON.parse(favorites) : {};
    const isAlreadyFavorite = Object.keys(payload).includes(`${postId}`);
    let newPayload;
    const component = document.querySelector('yb-details');
    if (isAlreadyFavorite) {
      delete payload[`${postId}`];
      newPayload = { ...payload };
      component?.shadowRoot?.getElementById('favorites')?.classList.remove('selected');
    }
    else {
      newPayload = { ...payload, ...newFavorite };
      component?.shadowRoot?.getElementById('favorites')?.classList.add('selected');

      if (product) {
        pushGA4EcommerceEvent('add_to_wishlist', {
          currency: product.currency,
          value: product.priceFrom,
          items: [buildGA4ItemFromProduct(product)],
        });
      }
    }
    window.localStorage.setItem('woody_favorites', JSON.stringify(newPayload));
    woodyCart.dispatchEvent(evt);
  }

  function loadFavoriteState() {
    const favorites = window.localStorage.getItem('woody_favorites');
    // @ts-expect-error globals is unknown
    const postId = window.globals?.post_id;
    const payload = favorites ? JSON.parse(favorites) : {};
    const isAlreadyFavorite = Object.keys(payload).includes(`${postId}`);
    if (isAlreadyFavorite) {
      const component = document.querySelector('yb-details');
      component?.shadowRoot?.getElementById('favorites')?.classList.add('selected');
    }
  }

  // SEO OPTIMIZATIONS
  function generateOpenGraphTags(activity: any, title: string | null = null) {
    if (activity === null)
      return;
    const properties = [
      { name: 'og:title', property: title ?? activity.name ?? '' },
      { name: 'og:description', property: '' },
      { name: 'og:image', property: activity.images?.[0]?.url ?? '' },
      { name: 'og:type', property: 'article' },
      { name: 'og:url', property: activity.bookingUrl ?? '' },
    ];

    const head = document.head;
    properties.forEach((property) => {
      if (property.property) {
        const metaTag = document.createElement('meta');
        metaTag.setAttribute('property', property.name);
        metaTag.setAttribute('content', property.property);
        head.appendChild(metaTag);
      }
    });
    const twitterCard = document.createElement('meta');
    twitterCard.setAttribute('name', 'twitter:card');
    twitterCard.setAttribute('content', 'summary_large_image');
    head.appendChild(twitterCard);
  }

  function setupJsonLd(activity: any, supplier: string | null = null) {
    if (activity === null)
      return;

    const div = document.createElement('div');
    const presentation = activity.descriptions?.find((it: any) => it.type === 'PRESENTATION') ?? '';
    div.insertAdjacentHTML('afterbegin', presentation.content);
    const description = div.textContent;

    const jsonLdData: any = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      'name': activity?.name,
      'image': activity?.images?.[0]?.url ?? 'https://images.unsplash.com/photo-1521138054413-5a47d349b7af?q=80&w=2303&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      'description': description,
      'brand': {
        '@type': 'Brand',
        'name': supplier ?? 'youBOOK',
      },
      'offers': {
        '@type': 'Offer',
        'url': window.location.href,
        'priceCurrency': activity?.currency,
        'price': activity?.priceFrom,
        'availability': 'https://schema.org/InStock',
      },
    };

    const ratingDetails: RatingData | undefined = activity.properties.find((it: any) => it.propertyName === 'rating')?.value;
    if (ratingDetails) {
      jsonLdData.aggregateRating = {
        '@type': 'AggregateRating',
        'ratingValue': ratingDetails.rating,
        'bestRating': ratingDetails?.maxRating ?? 10,
        'worstRating': 1,
        'reviewCount': ratingDetails.ratingReviewCount,
      };
    }

    const jsonLdScript = createJsonLdScript(jsonLdData);
    document.body.appendChild(jsonLdScript);
  }

  function createJsonLdScript(schema: any) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    return script;
  }
  return {
    scrollToWidget,
    scrollToFairguestWidget,
    dispatchFavoriteEvent,
    loadFavoriteState,
    generateOpenGraphTags,
    setupJsonLd,
  };
}
